import { Injectable, inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';

import { AnalyticsService } from 'src/app/core/services/analytics';
import { AccountState } from 'src/app/store/account';
import { DomainJoinState, type DomainJoinOffer } from 'src/app/store/domain-join';

/** Where the person accepted. */
export type DomainJoinSource = 'prompt' | 'my_blume';

/** What accepting did. */
export interface DomainJoinResult {
  /**
   * True when the person was already a member (a retry, or a join from
   * another device): nothing was created, and it is not counted as a join.
   */
  readonly alreadyMember: boolean;
}

/** The confirmation shown after accepting, true to what happened. */
export function domainJoinMessage(partnerName: string, result: DomainJoinResult): string {
  return result.alreadyMember ? `You're already a member of ${partnerName}.` : `Joined ${partnerName}!`;
}

/**
 * Accepts a domain-based invitation: joins the district as an Educator through
 * the `v3_acceptDomainJoin` Callable (which re-checks every rule), then follows
 * the ordinary invitation flow exactly - refresh the token for the new
 * membership claim, wait for the district to load, switch into it.
 *
 * Throws with a person-readable message when the server refuses (for example
 * the offer is no longer valid); callers show it. Resolves with whether the
 * person was already a member, so callers say so rather than "Joined".
 */
@Injectable({ providedIn: 'root' })
export class DomainJoinService {
  private readonly functions = inject(Functions);
  private readonly auth = inject(Auth);
  private readonly accountState = inject(AccountState);
  private readonly state = inject(DomainJoinState);
  private readonly analytics = inject(AnalyticsService);

  async accept(offer: DomainJoinOffer, source: DomainJoinSource): Promise<DomainJoinResult> {
    const startedAt = Date.now();
    let result: DomainJoinResult;
    try {
      result = await this.callAccept(offer.partnerId);
    } catch (error) {
      // A reason code only, never the message. The server sends why it
      // refused (details.reason); anything else reports its error code.
      this.analytics.trackFor(offer.partnerId, 'Domain Invitation Failed', {
        source,
        reason: failureReason(error),
      });
      // A refusal means the offer is no longer valid, so it goes. A network
      // or server error says nothing about the offer: keep it for a retry.
      if (isRefusal(error)) this.state.remove(offer.partnerId);
      const message = (error as { message?: string } | null)?.message;
      throw new Error(message || 'Could not join this team. Please try again.');
    }

    // Switch in first, and only then drop the offer: until the team is
    // loaded, the offer stays on screen with its spinner, so the popup and My
    // Blume change once (offer gone, team there) instead of emptying first.
    try {
      await this.auth.currentUser?.getIdToken(/* forceRefresh */ true);
      await this.waitForMembershipLoaded(offer.partnerId);
      await this.accountState.setActiveProfile(offer.partnerId);
    } finally {
      this.state.remove(offer.partnerId);
      // Once the switch is done (or has given up): the district is active,
      // so it files straight away, and the duration covers the whole wait.
      // A retry by someone already in is not another join.
      if (!result.alreadyMember) {
        this.analytics.trackFor(offer.partnerId, 'Domain Invitation Accepted', {
          source,
          duration_ms: Date.now() - startedAt,
        });
      }
    }
    return result;
  }

  /**
   * Call `v3_acceptDomainJoin`. A seam for specs: `httpsCallable` is a bare
   * module function, not something DI can replace.
   */
  protected async callAccept(partnerId: string): Promise<DomainJoinResult> {
    const call = httpsCallable<{ partnerId: string }, { ok: true; alreadyMember: boolean }>(
      this.functions,
      'v3_acceptDomainJoin',
    );
    const { data } = await call({ partnerId });
    return { alreadyMember: data?.alreadyMember === true };
  }

  /**
   * Wait, at most `timeoutMs`, for the joined district AND the person's
   * membership in it to reach AccountState, so the switch lands on a complete
   * team rather than filling in afterwards. Switching in goes ahead either way:
   * a slow load lands the person in the district a moment later.
   */
  private async waitForMembershipLoaded(partnerId: string, timeoutMs = 8000): Promise<void> {
    const deadline = Date.now() + timeoutMs;
    const isLoaded = () =>
      this.accountState.partners().some(partner => partner.id === partnerId) &&
      this.accountState.profiles().has(partnerId);
    while (!isLoaded() && Date.now() < deadline) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }
}

/** Why a join failed, as a short code: the server's refusal reason, else the error code. */
export function failureReason(error: unknown): string {
  const failure = error as { details?: { reason?: unknown }; code?: unknown } | null;
  if (typeof failure?.details?.reason === 'string') return failure.details.reason;
  if (typeof failure?.code === 'string') return failure.code.replace(/^functions\//, '');
  return 'unknown';
}

/** True when the server refused the join (the offer is no longer valid), not a network or server failure. */
export function isRefusal(error: unknown): boolean {
  const failure = error as { details?: { reason?: unknown }; code?: unknown } | null;
  if (typeof failure?.details?.reason === 'string') return true;
  const code = typeof failure?.code === 'string' ? failure.code.replace(/^functions\//, '') : '';
  return code === 'failed-precondition' || code === 'not-found';
}
